import styled from 'styled-components';

export const Section = styled.section`
  width: 100%;

  padding: var(--space-xxxxl) 0;

  background: var(--white);

  scroll-margin-top: 100px;

  @media screen and (max-width: 950px) {
    padding: var(--space-xxxl) var(--space-xl);
  }

  @media screen and (max-width: 600px) {
    padding: var(--space-xxl) var(--space-m);
  }
`;

export const Container = styled.div`
  width: min(100%, 1280px);

  margin: 0 auto;

  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(340px, 0.82fr);
  gap: var(--space-xxxxl);
  align-items: center;

  @media screen and (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: var(--space-xxxl);
  }
`;

export const Content = styled.div`
  max-width: 700px;
`;

export const Eyebrow = styled.p`
  margin: 0 0 var(--space-xs);

  color: var(--orange5);

  font-family: var(--mainFont);
  font-size: 0.78rem;
  font-weight: 850;

  text-transform: uppercase;
  letter-spacing: 0.14em;
`;

export const Title = styled.h2`
  max-width: 760px;

  margin: 0;

  color: var(--grey7);

  /* font-size: clamp(2.2rem, 4.5vw, 4rem); */
  font-size: 52px;
  line-height: 1.03;
  font-weight: 800;

  letter-spacing: -0.04em;

  @media screen and (max-width: 600px) {
    font-size: clamp(1.9rem, 8vw, 2.8rem);
  }
`;

export const Description = styled.p`
  max-width: 650px;

  margin: var(--space-m) 0 0;

  color: var(--grey4);

  font-family: var(--mainFont);
  font-size: 1rem;
  line-height: 1.8;
  font-weight: 500;

  @media screen and (max-width: 600px) {
    font-size: 0.95rem;
    line-height: 1.7;
  }
`;

export const FeatureList = styled.div`
  display: grid;
  gap: var(--space-m);

  margin-top: var(--space-xl);
`;

export const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--space-m);

  padding: var(--space-m);

  border: 1px solid var(--grey1);
  border-radius: var(--borderRadius);

  background: var(--cardBackground);

  box-shadow: var(--shadow-xs);

  transition:
    border-color 180ms ease,
    background-color 180ms ease,
    box-shadow 180ms ease;

  &:hover {
    border-color: rgba(234, 154, 38, 0.25);

    background: var(--white);

    box-shadow: var(--shadow-sm);
  }
`;

export const FeatureIcon = styled.div`
  width: 42px;
  height: 42px;

  flex-shrink: 0;

  display: grid;
  place-items: center;

  padding: var(--space-xs);

  border: 1px solid rgba(230, 139, 7, 0.14);
  border-radius: 50%;

  background: linear-gradient(135deg, var(--yellow1) 0%, var(--orange7) 100%);

  color: var(--orange5);

  font-size: 1rem;

  svg {
    display: block;
  }
`;

export const FeatureContent = styled.div`
  min-width: 0;
`;

export const FeatureTitle = styled.h3`
  margin: 0;

  color: var(--grey7);

  font-size: 1.05rem;
  line-height: 1.3;
  font-weight: 800;
`;

export const FeatureText = styled.p`
  margin: var(--space-xxs) 0 0;

  color: var(--grey4);

  font-family: var(--mainFont);
  font-size: 0.88rem;
  line-height: 1.6;
  font-weight: 500;
`;

export const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-s);

  margin-top: var(--space-xl);

  @media screen and (max-width: 520px) {
    flex-direction: column;
  }
`;

const sharedButtonStyles = `
  min-height: 50px;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-s);

  padding: 0.95rem 1.4rem;

  border-radius: var(--borderRadiusButton);

  font-family: var(--mainFont);
  font-size: 0.92rem;
  line-height: 1;
  font-weight: 800;
  text-decoration: none;

  transition:
    transform 180ms ease,
    border-color 180ms ease,
    background-color 180ms ease,
    color 180ms ease,
    box-shadow 180ms ease;

  &:hover {
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 3px solid var(--yellow3);
    outline-offset: 4px;
  }

  @media screen and (max-width: 520px) {
    width: 100%;
  }
`;

export const PrimaryButton = styled.a`
  ${sharedButtonStyles}

  border: 1px solid var(--grey7);

  background: var(--grey7);
  color: var(--white);

  box-shadow: var(--shadow-xs);

  &:hover {
    border-color: var(--orange5);

    background: var(--orange5);

    box-shadow: var(--shadow-sm);
  }
`;

export const SecondaryButton = styled.a`
  ${sharedButtonStyles}

  border: 1px solid var(--grey2);

  background: var(--white);
  color: var(--grey7);

  box-shadow: var(--shadow-xs);

  &:hover {
    border-color: var(--grey7);

    box-shadow: var(--shadow-sm);
  }
`;

export const ButtonIcon = styled.span`
  flex-shrink: 0;

  display: grid;
  place-items: center;

  transition: transform 180ms ease;

  svg {
    display: block;
  }

  ${PrimaryButton}:hover & {
    transform: translateX(3px);
  }
`;

export const GuidanceCard = styled.aside`
  padding: var(--space-xl);

  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--borderRadiusLarge);

  background: linear-gradient(150deg, var(--grey7) 0%, var(--grey6) 100%);

  box-shadow: var(--shadow-lg);

  @media screen and (max-width: 960px) {
    width: min(100%, 650px);
  }

  @media screen and (max-width: 600px) {
    padding: var(--space-l);
  }
`;

export const CardLabel = styled.span`
  width: fit-content;

  display: inline-flex;

  padding: 0.42rem 0.72rem;

  border-radius: var(--borderRadiusButton);

  background: var(--yellow4);
  color: var(--grey7);

  font-family: var(--mainFont);
  font-size: 0.7rem;
  font-weight: 800;

  text-transform: uppercase;
  letter-spacing: 0.09em;
`;

export const CardIcon = styled.div`
  width: 58px;
  height: 58px;

  display: grid;
  place-items: center;

  margin-top: var(--space-xl);

  border: 1px solid rgba(255, 210, 66, 0.5);
  border-radius: 50%;

  background: rgba(255, 210, 66, 0.08);
  color: var(--yellow4);

  font-size: 1.6rem;

  svg {
    display: block;
  }
`;

export const CardTitle = styled.h3`
  max-width: 390px;

  margin: var(--space-l) 0 0;

  color: var(--white);

  font-size: clamp(1.8rem, 3vw, 2.6rem);
  line-height: 1.05;
  font-weight: 800;

  letter-spacing: -0.035em;
`;

export const CardDescription = styled.p`
  max-width: 430px;

  margin: var(--space-m) 0 0;

  color: var(--grey1);

  font-family: var(--mainFont);
  font-size: 0.92rem;
  line-height: 1.7;
  font-weight: 500;
`;

export const CardSteps = styled.div`
  display: grid;
  gap: var(--space-m);

  margin-top: var(--space-xl);
`;

export const CardStep = styled.div`
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr);
  gap: var(--space-m);
  align-items: flex-start;

  padding-top: var(--space-m);

  border-top: 1px solid rgba(255, 255, 255, 0.13);
`;

export const StepNumber = styled.span`
  color: var(--yellow4);

  font-family: var(--mainFont);
  font-size: 1rem;
  line-height: 1;
  font-weight: 800;

  letter-spacing: 0.06em;
`;

export const StepContent = styled.div`
  min-width: 0;
`;

export const StepTitle = styled.h4`
  margin: 0;

  color: var(--white);

  font-size: 1rem;
  line-height: 1.3;
  font-weight: 800;
`;

export const StepText = styled.p`
  margin: var(--space-xxs) 0 0;

  color: var(--grey2);

  font-family: var(--mainFont);
  font-size: 0.82rem;
  line-height: 1.6;
  font-weight: 500;
`;
